import { ice } from '@halvard/rules';
import type { ShoveTone } from './hints.js';

/** Ice from thin (0) to a pressure ridge (3): near white, so the teal warriors stand out. */
export const ICE_COLORS = ['#B9CED8', '#D3E1E8', '#E6EEF2', '#F7FAFB'] as const;

export const SIDE_COLORS: Readonly<Record<ice.Side, { body: string; trim: string }>> = {
  brand: { body: '#E8742E', trim: '#3A1E10' },
  rime: { body: '#0F6E73', trim: '#F4F8FA' },
};

export const ICE_LOOK = {
  water: '#07121B',
  background: '#0D1124',
  crack: '#FFFFFF',
  /** A crack on thin ice: it will open a hole. */
  crackHole: '#FF4D5E',
  /** The dark groove under a crack's bright line. */
  crackShadow: '#1F3A52',
  stone: '#6F7479',
  stoneDark: '#3B4045',
  move: '#1B2C38',
  cursor: '#FFFFFF',
  selected: '#FFD27A',
  lastMove: '#E8742E',
} as const;

export const TONE_COLORS: Readonly<Record<ShoveTone, string>> = {
  works: '#FF5FA2',
  'sinks-enemy': '#FF5FA2',
  'sinks-ally': '#FFB020',
  fails: '#8A97A3',
};

/** Height of the slab top for ice level h (0–3). Water sits below thin ice. */
export function slabTop(h: number): number {
  return 0.18 + Math.max(h, -1) * 0.28;
}

/** Scale of a warrior model: size shows force. */
export const WARRIOR_SCALE: Readonly<Record<ice.WarriorType, number>> = {
  skier: 0.78,
  spear: 0.92,
  huscarl: 1.08,
};

export const WARRIOR_NAMES: Readonly<Record<ice.WarriorType, string>> = {
  skier: 'Skier',
  spear: 'Spear',
  huscarl: 'Huscarl',
};

export const SIDE_NAMES: Readonly<Record<ice.Side, string>> = { brand: 'Brand', rime: 'Rime' };

/** How a lost warrior is described, after its name: "Rime Skier is crushed against the ice". */
export const SINK_SENTENCES = {
  off: 'is shoved off the ice',
  hole: 'falls into a hole',
  fall: 'falls from the ledge and breaks through',
  crush: 'is crushed against the ice',
} as const;

export const RUNESTONE_NAMES = ['west runestone', 'centre runestone', 'east runestone'] as const;

/** The square each runestone's carved stone stands on: the centre of its floe. */
export const RUNESTONE_SQUARES = [
  { r: 4, c: 1 },
  { r: 4, c: 4 },
  { r: 4, c: 7 },
] as const;

/** Accessible label of a square, e.g. "e4, ice 2, Brand Huscarl, centre runestone". */
export function iceSquareLabel(state: ice.IceState, r: number, c: number): string {
  const name = `${'abcdefghi'[c] ?? '?'}${9 - r}`;
  const h = state.heights[r]?.[c] ?? ice.HOLE;
  const parts = [name, h === ice.HOLE ? 'hole' : `ice ${h}`];
  const warrior = ice.warriorAt(state, r, c);
  if (warrior) parts.push(`${SIDE_NAMES[warrior.side]} ${WARRIOR_NAMES[warrior.type]}`);
  const crack = state.cracks.find((k) => k.r === r && k.c === c);
  if (crack) {
    parts.push(
      `cracked, ${h === 0 ? 'opens a hole' : 'thins'} at the end of round ${crack.thawRound}`,
    );
  }
  const stone = ice.runestoneOf(r, c);
  if (stone >= 0) parts.push(RUNESTONE_NAMES[stone] ?? 'runestone');
  return parts.join(', ');
}
